import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Invoice, InvoiceStatus, ID } from '@shared/types';
import {
  buildBaseFields,
  generateCode,
  nextDocumentBranchNumber,
  writeAuditLog,
} from '@shared/utils';
import type { InvoiceCreateInput } from './invoice.service';

const prepaidInvoiceCreateSchema = z.object({
  customerId: z.string().min(1, '顧客は必須です'),
  orderId: z.string().min(1, '案件は必須です'),
  issueDate: z.instanceof(Timestamp),
  dueDate: z.instanceof(Timestamp),
  closingDate: z.instanceof(Timestamp).nullable().default(null),
  subtotal: z.number().min(0, '小計は0以上です'),
  taxAmount: z.number().min(0, '消費税は0以上です'),
  total: z.number().min(0, '合計は0以上です'),
  notes: z.string().nullable().default(null),
});

const COL = 'invoices';

export async function fetchPrepaidInvoices(filter?: {
  status?: InvoiceStatus;
}): Promise<Invoice[]> {
  try {
    let q = query(
      collection(db, COL),
      where('isDeleted', '==', false),
      where('paymentTiming', '==', 'prepaid'),
      orderBy('issueDate', 'desc')
    );
    if (filter?.status) {
      q = query(q, where('status', '==', filter.status));
    }
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Invoice);
  } catch (e) {
    throw e instanceof Error ? e : new Error('前払い請求書の取得に失敗しました');
  }
}

export async function fetchOverduePrepaidInvoices(): Promise<Invoice[]> {
  try {
    const now = Timestamp.now();
    const q = query(
      collection(db, COL),
      where('isDeleted', '==', false),
      where('paymentTiming', '==', 'prepaid'),
      where('prepaidDueDate', '<', now),
      where('status', 'in', ['draft', 'issued', 'partially_paid', 'overdue'])
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Invoice);
  } catch (e) {
    throw e instanceof Error ? e : new Error('期限切れ前払い請求書の取得に失敗しました');
  }
}

export async function createPrepaidInvoice(
  data: Omit<InvoiceCreateInput, 'paymentTiming' | 'prepaidDueDate'>,
  prepaidDueDate: Timestamp,
  userId: ID
): Promise<Invoice> {
  if (!prepaidDueDate) {
    throw new Error('前払い期限は必須です');
  }
  try {
    const validated = parseOrThrow(prepaidInvoiceCreateSchema, data);
    const branch = await nextDocumentBranchNumber('invoices', validated.orderId);
    const code = branch ?? (await generateCode('IN'));
    const payload = {
      ...validated,
      code,
      status: 'draft' as InvoiceStatus,
      paymentTiming: 'prepaid' as const,
      prepaidDueDate,
      paidAmount: 0,
      remainingAmount: validated.total,
      isLocked: false,
      ...buildBaseFields(userId),
    };
    const ref = await addDoc(collection(db, COL), payload);
    await writeAuditLog({
      entityType: 'invoice',
      entityId: ref.id,
      action: 'create',
      userId,
      after: { ...validated, paymentTiming: 'prepaid', prepaidDueDate },
    });
    return { id: ref.id, ...payload } as unknown as Invoice;
  } catch (e) {
    throw e instanceof Error ? e : new Error('前払い請求書の作成に失敗しました');
  }
}
